import '@brainhubeu/react-carousel/lib/style.css';

import Carousel from '@brainhubeu/react-carousel';
import PropTypes from 'prop-types';
import React, { memo } from 'react';
import styled, { css } from 'styled-components';

import ImageIcon from '../../../assets/img/ImageIcon';
import { Colors } from '../../styles/vars';
import { connect } from '../../utils/redux';
import ImageB64 from './ImageB64';
import PageTitle from './PageTitle';

const CarouselContainer = styled.div`
  grid-column: 1 / span 12;
  width: calc(100% + 80px);
  margin-left: -40px;

  .BrainhubCarousel__arrows {
    padding: 20px !important;
    background: none !important;

    span {
      border-color: ${Colors.black} !important;
    }
  }
`;

const NewsCard = styled.div`
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 40px;
  width: 100%;

  color: ${Colors.white};
  background: ${Colors.accent};

  ${props =>
    props.$imagePosition === 'left'
      ? css`
          .image-container {
            grid-column: 1 / span 7;
          }

          .content-container {
            grid-column: 8 / span 5;
            padding-right: 40px;
          }
        `
      : css`
          .image-container {
            grid-column: 6 / span 7;
          }

          .content-container {
            grid-column: 1 / span 5;
            padding-left: 40px;
          }
        `}

  .image-container {
    display: flex;
    justify-content: center;
    align-items: center;
    aspect-ratio: 660/400;
    grid-row: 1;

    background: rgba(0, 0, 0, 0.1);

    svg {
      opacity: 0.25;
    }

    img {
      width: 100%;
      height: 100%;

      object-fit: cover;
      object-position: center;

      user-select: none;
      pointer-events: none;
    }
  }

  .content-container {
    grid-row: 1;
    align-self: flex-end;
    padding-bottom: 40px;

    font-size: 14px;
    font-style: normal;
    font-weight: 650;
    line-height: normal;

    .euronews-card__title {
      svg {
      }

      .children {
        margin-left: 0.5em;
        color: ${Colors.white};
      }
    }
  }
`;

const NewsTagsContainer = styled.ul`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 0;
  margin-top: 1em;

  list-style: none;
`;

const NewsTag = styled.li`
  display: inline-flex;
  align-items: center;
  padding: 0.2em 0.6em;

  white-space: nowrap;
  font-weight: 400;
  background: ${Colors.white}33;
  border-radius: 1em;
`;

const getImagePosition = iImagePosition =>
  Number.parseInt(iImagePosition, 10) === 0 ? 'left' : 'right';

const EuroNewsImage = memo(
  ({ vImageLocalName, sImageWebPath }) => {
    if (vImageLocalName) {
      return <ImageB64 path={vImageLocalName} />;
    }

    if (sImageWebPath) {
      return <img src={sImageWebPath} alt={sImageWebPath} />;
    }

    return <ImageIcon size="5em" />;
  },
  (prev, next) =>
    prev.vImageLocalName === next.vImageLocalName &&
    prev.sImageWebPath === next.sImageWebPath,
);

EuroNewsImage.propTypes = {
  sImageWebPath: PropTypes.string,
  vImageLocalName: PropTypes.string,
};

EuroNewsImage.defaultProps = {
  sImageWebPath: null,
  vImageLocalName: null,
};

const EuroNews = ({ records }) => {
  if (!records || !Array.isArray(records) || records.length <= 0) {
    return null;
  }

  return (
    <>
      <PageTitle fromComponent="h2">Actualités</PageTitle>

      {records && (
        <CarouselContainer>
          <Carousel
            autoPlay={10_000}
            slidesPerPage={1}
            infinite
            stopAutoPlayOnHover
            keepDirectionWhenDragging
            arrows
            dots
          >
            {records.map(
              ({
                kIDEuroNews,
                sTitle,
                sTextOrHtml,
                iImagePosition,
                sImageWebPath,
                vImageLocalName,
                sTag,
              }) => (
                <NewsCard
                  key={kIDEuroNews}
                  $imagePosition={getImagePosition(iImagePosition)}
                >
                  <div className="image-container">
                    <EuroNewsImage
                      sImageWebPath={sImageWebPath}
                      vImageLocalName={vImageLocalName}
                    />
                  </div>

                  <div className="content-container">
                    <PageTitle
                      fromComponent="h3"
                      className="euronews-card__title"
                      margin="0 0 1em 0"
                      fontSize="22px"
                    >
                      {sTitle}
                    </PageTitle>

                    <div
                      className="html-content"
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: sTextOrHtml }}
                    />

                    {sTag && (
                      <NewsTagsContainer className="tags-container">
                        {sTag.split(',').map((tag, index) => (
                          <NewsTag
                            key={[index, tag].join('--')}
                            className="tag"
                          >
                            #{tag.trim()}
                          </NewsTag>
                        ))}
                      </NewsTagsContainer>
                    )}
                  </div>
                </NewsCard>
              ),
            )}
          </Carousel>
        </CarouselContainer>
      )}
    </>
  );
};

EuroNews.propTypes = {
  records: PropTypes.arrayOf(
    PropTypes.shape({
      kIDEuroNews: PropTypes.string.isRequired,
      sTitle: PropTypes.string.isRequired,
      sTextOrHtml: PropTypes.string.isRequired,
      iImagePosition: PropTypes.oneOf(['0', '1']).isRequired,
      sImageWebPath: PropTypes.string,
      vImageLocalName: PropTypes.string,
      sTag: PropTypes.string,
    }),
  ),
};

EuroNews.defaultProps = {
  records: null,
};

export default connect(state => ({ ...state.euroNews }), {})(EuroNews);
